/**
 * §approval-queue — the operator's queue of actions the agent prepared but did
 * not perform.
 *
 * Every industry page on orikin.ai ends with "…never leaves without a person
 * tapping approve". This is the client for the tap. The agent-side gate lives
 * in approval_queue.gate on the backend: a gated action is PREPARED, never
 * executed, and only runs when someone here decides.
 */
import { api } from "@/lib/api";

export type ApprovalStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "executed"
  | "failed";

export interface PendingAction {
  id: number;
  action: string;
  /** What would actually run, verbatim. Shown so the operator approves the
   *  real thing rather than a summary of it. */
  params: Record<string, unknown>;
  /** Why it was asked for — channel, caller, what the customer actually said.
   *  A row you cannot decide from without replaying the call is a row that
   *  sits in the queue forever. */
  evidence: Record<string, unknown>;
  status: ApprovalStatus;
  requested_at: string;
  decided_at?: string;
  decided_by?: string;
  result?: Record<string, unknown>;
  error?: string;
}

export interface ApprovalsResponse {
  tenant_id: string;
  status: string;
  count: number;
  actions: PendingAction[];
}

export async function listApprovals(
  status: ApprovalStatus | "all" = "pending",
): Promise<ApprovalsResponse> {
  return api<ApprovalsResponse>(
    `/api/approvals?status=${encodeURIComponent(status)}`,
  );
}

export interface DecideResult {
  ok: boolean;
  id: number;
  action: string;
  status: ApprovalStatus;
  decided_by: string;
  result?: Record<string, unknown>;
  error?: string;
}

/**
 * Approve (and run) or reject.
 *
 * The backend answers 409 when the row was already decided — a double-click,
 * a retry, or another operator getting there first. `api` throws on non-2xx,
 * so callers should surface that message rather than silently refreshing:
 * telling the loser of a race that it worked is worse than the race.
 */
export async function decideApproval(
  id: number,
  decision: "approve" | "reject",
): Promise<DecideResult> {
  return api<DecideResult>(`/api/approvals/${id}/decide`, {
    method: "POST",
    body: { decision },
  });
}
